// Seated-item hover — the kind for a product standing on a HOLDER'S SURFACE (a pedestal's
// support-top, a counter, a shelf) that is not a thing you read.
//
// ASK-137 §74 F-115k. E2E115 step 6, Vanessa's words: *"in Play the robot had no 'E'; the two other
// items on the pedestal could not be interacted with either."* The customer dispatcher is per FIXTURE
// KIND — vinyl-bin, stack-table, pile-table, book-row, spinner-rack, wall-comic, vinyl-wall-shelf,
// longbox-lid, bailey — and nothing in that list claims a support-top seat, so everything standing on
// a pedestal was scenery whatever its type. MEASURED on the plane before the fix (store-test-2, her
// pedestal, customer session): all three items seat-stamped, `userData.interactable` **null**, and
// `interactiveMeshes` / `wallComics` / `pileComics` / `bookEntries` all **0**.
//
// 🔴 WHY THIS KIND IS NOT "support-top". The card named it that, and a surface is the wrong subject:
// the verbs belong to the PRODUCT, not to the furniture it stands on. A figurine on a counter and a
// figurine on a shelf are one interaction; a book on a pedestal is a different one and already has
// its pattern. So readable products on a surface seat are wired to the EXISTING seated-comic path
// (`_wireSeatedComicInteraction`, the route the selling-space floor already uses and which
// probe-zone-customer and probe-floor-stock hold), and this kind takes what is left: the things you
// pick up and turn over. That is the standing note §74 quotes — patterns are per product type.
//
// Precedence sits BELOW wall-comic (50) so a readable item under the cursor keeps its own verbs, and
// the hover is raycast-then-walk-up for the reason wall-comic records: a catalogue product is a
// multi-mesh clone, so hitting any part of it must resolve to the registered ITEM.

import { registerInteractable } from "../_interactable-registry.js";
import { addOutline, removeOutline } from "../helpers.js";

registerInteractable({
	id: "seated-item",
	precedence: 45,
	// ASK-137 §84 — THIS KIND'S OWN PROMPT AND SUBJECT. `stage.js` used to enumerate every kind by
	// hand, twice (`_updateEHint`'s hover ladder and `_hoveredProduct`), which is exactly what the
	// registry's own header says a registry exists to end. The prompt a shopper reads and the product
	// it names belong where the rest of this kind's behaviour already is.
	hoverHint: '<kbd>E</kbd> Pick Up',
	productOf(stage, active) {
		return active?.userData?.interactable?.product ?? null;
	},

	suppressedBy(stage) {
		if (stage.activeFixtureHero) return true;          // something is already in hand
		if (stage.boxState === "open" || stage.boxState === "browsing" || stage.boxState === "hero") return true;
		if (stage.activeVinylBin || stage.activeStackTable) return true;
		if (stage.hoveredWallComic) return true;           // a readable item under the cursor wins
		if (!stage.seatedItems?.length) return true;
		return false;
	},

	evaluateActive(stage) {
		if (!stage.seatedItems?.length) return null;
		const hits = stage.raycaster.intersectObjects(stage.seatedItems.map((i) => i.root), true);
		for (const h of hits) {
			for (let n = h.object; n; n = n.parent) {
				if (n.userData?.seatedItemIdx != null) return n;
			}
		}
		return null;
	},

	commitHover(stage, prev, next) {
		if (prev === next) return;
		if (prev) removeOutline(prev);
		stage.hoveredSeatedItem = next;
		if (next) addOutline(next);
	},

	interactE(stage, root) {
		if (!root || root.userData?.seatedItemIdx == null) return false;
		// The OPEN pattern is lazy (`_item-hero.js`): bringing a statue to the camera, orbiting it and
		// drawing its card is a few hundred bytes of code that runs when a shopper presses E on one
		// particular kind of thing, and the customer bundle is at its ceiling. What stays eager is
		// this branch and the hover above — the same split `_boot-load-error.js` is built on.
		void stage.openSeatedItem(root.userData.seatedItemIdx);
		return true;
	},
});
